<blaze-dialog
  #dialog
  (closeDialog)="onCloseDialog()"
  [contentClass]="'user-profile-dialog-content'"
  [contentHasPadding]="true"
  [showFooter]="false"
  class="two-factor-auth-setup-dialog"
>
  <ng-container icon>
    <mat-icon class="menu-icon" svgIcon="icon_var_user_16" />
  </ng-container>

  <ng-container class="title" title>
    @if (disableTwoFactorAuth) {
      Disable Two-Factor Authentication
    } @else if (emergencyBackupCode()) {
      Two-Factor Authentication Backup Code
    } @else {
      Two-Factor Authentication
    }
  </ng-container>

  <ng-container content>
    <div class="tfa-wizard" [class.settings-visible]="changeToken() && !emergencyBackupCode()">
      <div class="tfa-wizard__content">
        <!-- Disable two factor authentication -->
        @if (disableTwoFactorAuth) {
          <div class="tfa-description">
            Please provide your account password to disable Two-Factor Authentication.
          </div>
          <form class="tfa-form" [formGroup]="passwordForm" (ngSubmit)="onDisableTwoFactorAuth()">
            <div class="tfa-form__input password">
              <label for="password">
                <h3>Account Password</h3>
              </label>
              <input
                #passwordInput
                autocomplete="current-password"
                class="bd-text-input tall"
                formControlName="password"
                type="password"
                blazePasswordVisibilityToggle="pb-0"
              />
            </div>
            <div class="tfa-form__input action-button flex-1">
              <button
                [disabled]="!passwordForm.valid || isApiCallInProgress()"
                blazeButton="primary"
                blazeButtonSize="xl"
                class="uppercase"
              >
                Disable
              </button>
            </div>
          </form>
        } @else {
          <!-- Enable two factor authentication (no change token) -->
          @if (!changeToken()) {
            <div class="tfa-description">
              Two-Factor Authentication adds an extra layer of protection to your account. Whenever
              you sign in to your Blaze account, you will need to enter both your password and also
              a security code sent to your mobile phone.
            </div>
            <form class="tfa-form" [formGroup]="passwordForm" (ngSubmit)="onGetChangeToken()">
              <div class="tfa-form__input password">
                <label for="password">
                  <h3>Account Password</h3>
                </label>
                <input
                  #passwordInput
                  autocomplete="current-password"
                  class="bd-text-input tall"
                  formControlName="password"
                  type="password"
                  blazePasswordVisibilityToggle="pb-0"
                />
              </div>
              <div class="tfa-form__input action-button flex-1">
                <button
                  [disabled]="!passwordForm.valid"
                  blazeButton="primary"
                  blazeButtonSize="xl"
                  class="uppercase"
                >
                  Next
                </button>
              </div>
            </form>
          }

          <!-- Edit two factor authentication (change token fetched): show two-factor settings -->
          @if (changeToken() && !emergencyBackupCode()) {
            <h3 class="text-center"> How would you like to receive your security codes? </h3>
            <div class="tfa-auth-types">
              <button
                class="tfa-auth-type"
                [class.selected]="authType() === 'Sms'"
                (click)="onSetAuthType('Sms')"
              >
                <strong>Use Text Message</strong>
                <span>Security codes will be sent to your mobile phone</span>
              </button>
              <button
                class="tfa-auth-type"
                [class.selected]="authType() === 'Authenticator'"
                (click)="onSetAuthType('Authenticator')"
              >
                <strong>Use A Mobile App</strong>
                <span>Security codes will be generated by an authenticator app</span>
              </button>
            </div>

            <div class="tfa-inner-container relative">
              <!-- Phone number / SMS setup -->
              @if (authType() === 'Sms') {
                <div class="flex flex-col gap-8 mt-24">
                  <h3>Enter your mobile number to set up</h3>
                  <p> We will send a security code to this mobile whenever you sign in to Blaze </p>
                </div>

                <form
                  (ngSubmit)="onSendCodeToMobile()"
                  [formGroup]="phoneForm"
                  class="tfa-form phone"
                >
                  <div class="tfa-form__input flex-1">
                    <label for="country">Country</label>
                    <button
                      [matMenuTriggerFor]="optionsMenu"
                      class="country-selector-trigger bd-text-input tall"
                      id="country"
                      type="button"
                    >
                      <span> {{ country().name }} +{{ country().dialCode }} </span>
                      <mat-icon svgIcon="chevron_down" />
                    </button>
                    <mat-menu #optionsMenu="matMenu" class="max-h-[400px]">
                      @for (c of countries; track c) {
                        <button class="tfa-country-btn" (click)="onSetCountry(c)">
                          {{ c.name }} +{{ c.dialCode }}
                        </button>
                      }
                    </mat-menu>
                  </div>
                  <div class="tfa-form__input flex-1">
                    <label for="phone"> Mobile number </label>
                    <div class="input-with-prefix">
                      <span class="bd-text-input tall"> +{{ country().dialCode }} </span>
                      <input
                        autocomplete="tel-local"
                        class="bd-text-input tall"
                        formControlName="phone"
                        style="line-height: 16px !important"
                        id="phone"
                      />
                    </div>
                  </div>
                  <div class="tfa-form__input action-button">
                    <button
                      [blazeButton]="isCodeSent() ? 'secondary' : 'primary'"
                      [disabled]="!phoneForm.valid"
                      blazeButtonSize="xl"
                      class="uppercase"
                    >
                      {{ isCodeSent() ? 'Resend code' : 'Send code' }}
                    </button>
                  </div>
                </form>

                @if (isCodeSent()) {
                  <form
                    class="tfa-form code"
                    [formGroup]="codeForm"
                    (ngSubmit)="onVerifyAuthCode()"
                  >
                    <div class="tfa-form__input code">
                      <label for="code">
                        <h3>Enter security code to enable Two-Factor Authentication</h3>
                      </label>
                      <div class="input-with-btn">
                        <input
                          autocomplete="off"
                          class="bd-text-input tall"
                          formControlName="code"
                          id="code"
                          type="text"
                        />
                        <button
                          [disabled]="!codeForm.valid"
                          blazeButton="primary"
                          blazeButtonSize="xl"
                        >
                          Enable two-factor authentication
                        </button>
                      </div>
                    </div>
                  </form>
                }
              }

              <!-- Authenticator setup -->
              @if (authType() === 'Authenticator' && !authenticatorCode()) {
                <blaze-element-loading-indicator [isVisible]="true" />
              } @else if (authType() === 'Authenticator' && authenticatorCode()) {
                <div class="flex flex-col gap-8 mt-24">
                  <h3>Scan Or Enter Code</h3>
                  <p>
                    Download an authenticator app, tap on the "add" button, and scan this barcode to
                    set up your account.
                    <a (click)="onToggleEnterCodeManually()">
                      {{
                        enterCodeManually()
                          ? 'Scan barcode image instead'
                          : 'Enter code manually instead'
                      }}
                    </a>
                  </p>
                </div>

                @if (!enterCodeManually()) {
                  <div class="flex flex-1 items-center justify-center">
                    <div class="tfa-image-code">
                      <img [src]="authenticatorCode()?.codeImage" alt="authenticaor code" />
                    </div>
                  </div>
                }
                @if (enterCodeManually()) {
                  <div class="tfa-code">
                    {{ authenticatorCode()?.code }}
                  </div>
                }
                <form class="tfa-form code" [formGroup]="codeForm" (ngSubmit)="onVerifyAuthCode()">
                  <div class="tfa-form__input code">
                    <label for="code">
                      <h3>Enter security code to enable Two-Factor Authentication</h3>
                    </label>
                    <div class="input-with-btn">
                      <input
                        autocomplete="off"
                        class="bd-text-input tall"
                        formControlName="code"
                        id="code"
                        type="text"
                      />
                      <button
                        [disabled]="!codeForm.valid"
                        blazeButton="primary"
                        blazeButtonSize="xl"
                      >
                        Enable two-factor authentication
                      </button>
                    </div>
                  </div>
                </form>
              }
            </div>
          }

          @if (emergencyBackupCode()) {
            <div class="tfa-description">
              This is your backup code. Please keep it somewhere safe.
            </div>
            <div class="flex flex-row gap-8 items-center mt-8">
              <div class="tfa-code backup-code">
                {{ emergencyBackupCode() }}
              </div>
              <button
                blazeButton="secondary"
                blazeButtonSize="xl"
                class="uppercase"
                (click)="onCopyBackupCode()"
              >
                Copy
              </button>
            </div>
            <div class="flex flex-1 justify-end mt-24">
              <button blazeButton="primary" class="uppercase" (click)="onCloseDialog()">
                Ready
              </button>
            </div>
          }
        }
      </div>
    </div>
  </ng-container>
</blaze-dialog>
